<!-- A snackbar to confirm/abort a selection of RadioSpots/TVSpotBundles/Tickers -->

<template>
  <v-snackbar
    v-model="show"
    class="
      contentselectionoverlay
      fixed-snackbar-z-index
      v-snackbar--auto-height v-snackbar--no-top-left-right-padding
    "
    color="grey lighten-4"
    multi-line
    vertical
    :class="$desktopModeStyles"
    :timeout="0"
    :dark="false"
  >
    <v-layout
      class="mb-3"
      align-center
    >
      <!-- Mode Selector -->
      <v-select
        v-model="mode"
        data-cy="contentselection-mode-select"
        :items="selectableModes"
        hide-details
        :menu-props="{'offset-y': true, top: true}"
        class="mt-0 pl-3 pr-3"
        :style="{'max-width': contentMaxWidthCSS}"
      >
        <template
          slot="item"
          slot-scope="data"
        >
          <v-list-tile-avatar
            v-if="
              data.item.icon ||
                data.item.value == CONTENT_SELECTION_MODES.bundle
            "
          >
            <v-icon
              v-if="data.item.icon"
              :class="{'msr--text': data.item.value == mode}"
              :disabled="data.item.disabled"
            >
              {{ data.item.icon }}
            </v-icon>
            <multiformat-bundle-indicator
              v-if="data.item.value == CONTENT_SELECTION_MODES.bundle"
              is-multiformat-bundle
              :disabled="data.item.disabled"
              :color-class="
                !data.item.disabled && data.item.value === mode
                  ? 'msr--text'
                  : 'grey--text text--darken-2'
              "
            />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title
              :class="{'grey--text text--lighten-1': data.item.disabled}"
            >
              {{ data.item.text }}
            </v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>
      <!-- Button to abort -->
      <v-btn
        class="ml-auto mr-2 mt-2 mb-0"
        :disabled="loading"
        flat
        icon
        color="black"
        data-cy="content-selection-overlay-abort-btn"
        style="min-width: 36px"
        :title="$t('abort')"
        @click="$emit('aborted')"
      >
        <v-icon>close</v-icon>
      </v-btn>
    </v-layout>
    <v-layout
      column
      align-center
      wrap
    >
      <span
        class="black--text"
        :style="{'max-width': contentMaxWidthCSS}"
      >{{ title }}.</span>
      <v-layout align-end>
        <v-spacer />
        <btn-ok
          round
          :icon="modeToButtonIcon[mode]"
          class="fixed-snackbar-btn-margin"
          :disabled="shouldDisableConfirmButton"
          :loading="loading"
          data-cy="content-selection-overlay-confirm-btn"
          @click="$emit('selectionconfirmed', mode, selectedContents)"
        />
        <v-spacer />
      </v-layout>
    </v-layout>
  </v-snackbar>
</template>

<script>
import {capitalize} from '@/utils';

import {CONTENT_SELECTION_MODES} from '@/mixins/contentSelection.js';
import contentSelection from '@/mixins/contentSelection.js';

import BtnOk from '@/components/ButtonOk';
import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import {getMainTVSpotFromBundle} from '../utils';

export default {
  components: {
    BtnOk,
    MultiformatBundleIndicator,
  },
  mixins: [contentSelection],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    /** Note: We decided to not have a showToggleAllCheckbox as discussed in review 29.10.2019 */
    loading: {
      type: Boolean,
      required: false,
    },
    /* A list of contents (not yet filtered by .selected) */
    contents: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      CONTENT_SELECTION_MODES: CONTENT_SELECTION_MODES,
      modeToButtonIcon: {
        [CONTENT_SELECTION_MODES.activate]: 'arrow_forward',
        [CONTENT_SELECTION_MODES.deactivate]: 'block',
        [CONTENT_SELECTION_MODES.delete]: 'delete',
        [CONTENT_SELECTION_MODES.refreshDate]: 'refresh',
      },
    };
  },
  computed: {
    mode: {
      get() {
        return this.$store.state.common.contentSelectionOverlayMode;
      },
      set(mode) {
        this.$store.commit('SET_CONTENT_SELECTION_OVERLAY_MODE', mode);
      },
    },
    shouldDisableConfirmButton() {
      if (this.selectedMode && this.selectedMode.disabled) {
        return true;
      }

      if (this.mode == this.CONTENT_SELECTION_MODES.bundle) {
        return this.selectedTVSpotBundles.length <= 1;
      }

      return this.selectedContents.length === 0;
    },
    contentMaxWidthCSS() {
      return this.$vuetify.breakpoint.smAndUp ? '288px' : 'initial';
    },
    onlyTaggedContentSelected() {
      return (
        this.selectedContents.length > 0 &&
        this.selectedContents.filter((selectedContent) => {
          let content = selectedContent;
          if (content.kind === 'tvspotbundle') {
            content = getMainTVSpotFromBundle(content);
          }
          return content.is_tagged_content;
        }).length == this.selectedContents.length
      );
    },
    /**
     * Get full object of current mode instead of just the name constant.
     *
     * E.g. to check if it is disabled.
     */
    selectedMode() {
      return this.selectableModes.filter(
        (selectableMode) => selectableMode.value == this.mode
      )[0];
    },
    selectableModes() {
      // We can fully hide modes here, or just disable them further down.
      return (
        Object.keys(this.CONTENT_SELECTION_MODES)
          // Disallow mode if current User definitely cannot use it.
          .filter((modeKey) => {
            // Deleting content requires that User is a content manager.
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.delete
            ) {
              return this.$store.getters.userManagesContentForAtLeastOneGroup;
            }
            // Bundling content requires that User is a content manager.
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.bundle
            ) {
              return this.$store.getters.userManagesContentForAtLeastOneGroup;
            }
            // Refreshing date of content requires that User is a content manager.
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.refreshDate
            ) {
              return this.$store.getters.userManagesContentForAtLeastOneGroup;
            }
            return true;
          })
          .map((modeKey) => {
            // Disable the mode if the current selection makes no sense for it.
            //
            let modeIsDisabled = false;

            // Deleting is only enabled with at least one deletable content.
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.delete
            ) {
              const contentGroupIds = this.$store.state.common.contents.map(
                (content) => content.group_id
              );
              modeIsDisabled = !this.$store.getters.uploadgroups.some(
                (uploadgroup) => {
                  return contentGroupIds.includes(uploadgroup.id);
                }
              );
            }

            // Activation
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.activate
            ) {
              // If we only have tagged content selected, activation
              // won't do anything. Note: We support deactivation for
              // tagged content since 01/2024.
              if (this.onlyTaggedContentSelected) {
                modeIsDisabled = true;
              }
            }

            // Bundling
            if (
              this.CONTENT_SELECTION_MODES[modeKey] ===
              this.CONTENT_SELECTION_MODES.bundle
            ) {
              // We need to have multiple bundles selected.
              if (this.selectedTVSpotBundles.length < 2) {
                modeIsDisabled = true;
              }

              // Each should contain one TVSpot and we don't want duplicate
              // TVFormats. Also all bundles must be within the same Group.
              const seenGroupIds = [];
              const seenTVFormatIds = [];
              for (const bundle of this.selectedTVSpotBundles) {
                if (bundle.has_multiple_formats) {
                  modeIsDisabled = true;
                  break;
                }
                const mainTVSpot = getMainTVSpotFromBundle(bundle);
                seenGroupIds.push(mainTVSpot.group.id);

                const notAllFromSameGroup = new Set(seenGroupIds).length > 1;
                const duplicateTVFormat =
                  seenTVFormatIds.indexOf(mainTVSpot.tvformat.id) !== -1;

                if (notAllFromSameGroup || duplicateTVFormat) {
                  modeIsDisabled = true;
                }
                seenTVFormatIds.push(mainTVSpot.tvformat.id);
              }
            }

            const modeValue = this.CONTENT_SELECTION_MODES[modeKey];
            return {
              value: modeValue,
              // Examples, so it can be found in code search:
              // - 'contentSelectionModeActivate'
              // - 'contentSelectionModeBundle'
              // - 'contentSelectionModeDeactivate'
              // - 'contentSelectionModeDelete'
              // - 'contentSelectionModeRefreshDate'
              text: this.$t('contentSelectionMode' + capitalize(modeKey)),
              mode: modeKey,
              icon: this.modeToButtonIcon[modeValue],
              disabled: modeIsDisabled,
            };
          })
      );
    },
    title() {
      // Default: MODE_ACTIVATE
      let noneSelected = this.$t('activateMultipleContentsTogetherPlease');
      let oneSelected = this.$t('activateMultipleContentsTogetherNumSingular');
      let multipleSelected = this.$t('activateMultipleContentsTogetherNum');

      if (this.mode === this.CONTENT_SELECTION_MODES.deactivate) {
        noneSelected = this.$t('deactivateMultipleContentsTogetherPlease');
        oneSelected = this.$t('deactivateMultipleContentsTogetherNumSingular');
        multipleSelected = this.$t('deactivateMultipleContentsTogetherNum');
      } else if (this.mode === this.CONTENT_SELECTION_MODES.delete) {
        noneSelected = this.$t('deleteMultipleContentsTogetherPlease');
        oneSelected = this.$t('deleteMultipleContentsTogetherNumSingular');
        multipleSelected = this.$t('deleteMultipleContentsTogetherNum');
      } else if (this.mode === this.CONTENT_SELECTION_MODES.bundle) {
        noneSelected = this.$t('bundleMultipleContentsTogetherPlease');
        oneSelected = this.$t('bundleMultipleContentsTogetherNumSingular');
        multipleSelected = this.$t('bundleMultipleContentsTogetherNum');
      } else if (this.mode === this.CONTENT_SELECTION_MODES.refreshDate) {
        noneSelected = this.$t('refreshDateMultipleContentsTogetherPlease');
        oneSelected = this.$t('refreshDateMultipleContentsTogetherNumSingular');
        multipleSelected = this.$t('refreshDateMultipleContentsTogetherNum');
      }

      if (this.selectedContents.length === 0) {
        return noneSelected;
      } else if (this.selectedContents.length === 1) {
        return oneSelected;
      }
      return multipleSelected.replace(
        '{{ num }}',
        this.selectedContents.length
      );
    },
  },
  watch: {
    selectedContents: {
      deep: true,
      handler: function () {
        if (this.atLeastOneContentSelected) {
          this.$store.commit(
            'SET_PRE_ROUTE_QUESTION',
            this.$t('selectedSpotsRouteConfirm')
          );
        } else {
          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
        }
      },
    },
  },
};
</script>

<style scoped>
.v-snackbar--auto-height >>> .v-snack__content {
  height: auto;
}

.contentselectionoverlay.v-snackbar--no-top-left-right-padding
  >>> .v-snack__content {
  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
}
</style>

<style>
.v-snack__content .v-btn.fixed-snackbar-btn-margin {
  margin: 8px !important;
  margin-bottom: 0px !important;
}

/**
 *  v-dialog has a z-index of 201 as of Vuetify 0.17, snackbars
 *  have one of 1000. Reduce it so the overlay is behind any
 *  fullscreen dialogs.
 */
.fixed-snackbar-z-index {
  z-index: 201;
}
</style>



// WEBPACK FOOTER //
// src/components/ContentSelectionOverlay.vue